import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, FileText, RefreshCcw, Trash2, X, ChevronRight, AlertTriangle, History, Eraser,
} from "lucide-react";
import { api, useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, LoadingState, EmptyState } from "@/components/app/primitives";
import { LiveBadge, SourceTag, ReviewStateChip, QUALITY_TONE, REQUEST_TONE, fmtInt } from "@/components/app/livePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const FIELD_LABELS = {
  source_owner: "Source owner", coverage_start: "Coverage start (YYYY-MM)", coverage_end: "Coverage end (YYYY-MM)",
};

const UploadPanel = ({ meta, onUploaded }) => {
  const { roleKey } = useApp();
  const inputRef = useRef(null);
  const [staged, setStaged] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    source_owner: "Brightspeed", coverage_start: "", coverage_end: "",
    classification: "confidential", business_domain: "operations",
    journey_stage: "cross_journey", related_metric: "order_cancellation_rate",
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stage = (file) => {
    if (!file) return;
    const ok = [".csv", ".xlsx", ".pdf", ".docx", ".txt"].some((e) => file.name.toLowerCase().endsWith(e));
    if (!ok) { toast.error("Unsupported file type. Allowed: CSV, XLSX, PDF, DOCX, TXT."); return; }
    setStaged(file);
  };

  const submit = async () => {
    if (!staged) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", staged);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("actor_role", roleKey);
      await api.post("/live/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${staged.name} registered - profiling started`);
      setStaged(null);
      onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Register a Brightspeed file" testId="intake-upload-panel"
      right={<span className="text-[10px] font-mono text-[--tx-3]">CSV · XLSX · PDF · DOCX · TXT</span>}>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); stage(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="intake-dropzone"
        className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${dragOver ? "border-[--ai] bg-[--ai-soft]" : "border-[--hairline] bg-[--shell-2] hover:border-[--ai]"}`}
      >
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.pdf,.docx,.txt" className="hidden"
          data-testid="intake-file-input" onChange={(e) => stage(e.target.files?.[0])} />
        <UploadCloud size={20} className="mx-auto text-[--ai] mb-1.5" />
        {staged ? (
          <div className="text-[13px] font-semibold text-[--tx-1]" data-testid="intake-staged-filename">{staged.name}
            <span className="text-[--tx-3] font-normal ml-2">{(staged.size / 1024).toFixed(0)} KB</span>
          </div>
        ) : (
          <div className="text-[13px] text-[--tx-2]">Drop a file here or click to browse</div>
        )}
        <div className="text-[11px] text-[--tx-3] mt-0.5">Stored in the secure engagement workspace - never blended with demonstration data</div>
      </div>

      <div className="grid md:grid-cols-3 gap-2.5 mt-3">
        {["source_owner", "coverage_start", "coverage_end"].map((k) => (
          <div key={k}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">{FIELD_LABELS[k]}</div>
            <Input value={form[k]} onChange={(e) => setField(k, e.target.value)}
              placeholder={k.startsWith("coverage") ? "2026-08" : ""}
              className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid={`intake-field-${k}`} />
          </div>
        ))}
        {[
          { k: "classification", opts: meta?.classifications || [] },
          { k: "business_domain", opts: meta?.business_domains || [] },
          { k: "journey_stage", opts: meta?.journey_stages || [] },
        ].map(({ k, opts }) => (
          <div key={k}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1 capitalize">{k.replace("_", " ")}</div>
            <Select value={form[k]} onValueChange={(v) => setField(k, v)}>
              <SelectTrigger className="h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid={`intake-select-${k}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[--shell-2] border-[--hairline]">
                {opts.map((o) => <SelectItem key={o} value={o} className="text-xs capitalize">{o.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-[10px] font-mono text-[--tx-3]">Related metric: order_cancellation_rate</div>
        <button onClick={submit} disabled={!staged || busy} data-testid="intake-register-button"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-40 active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
          {busy ? "Registering..." : "Register & profile"}
        </button>
      </div>
    </Panel>
  );
};

const FileDrawer = ({ fileId, onClose, onChanged }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { roleKey } = useApp();

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/live/files/${fileId}`);
      setFile(res.data);
    } catch (e) { toast.error("Could not load file detail"); }
    setLoading(false);
  }, [fileId]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/live/files/${fileId}?actor_role=${roleKey}`);
      toast.success("File removed from the engagement");
      onClose(); onChanged();
    } catch (e) { toast.error("Delete failed"); }
  };

  const prof = file?.profile;
  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="intake-file-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.18 }}
        className="relative w-[640px] max-w-[92vw] h-full bg-[--shell-1] border-l border-[--hairline] overflow-y-auto thin-scroll">
        <div className="sticky top-0 bg-[--shell-1] border-b border-[--divider] px-5 py-3 flex items-center justify-between z-10">
          <div className="min-w-0">
            <div className="font-display text-sm font-semibold text-[--tx-1] truncate" data-testid="drawer-filename">{file?.filename || "..."}</div>
            {file && <SourceTag file={file} />}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={remove} data-testid="drawer-delete-button" title="Remove file"
              className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] hover:text-[--danger] transition-colors">
              <Trash2 size={13} />
            </button>
            <button onClick={onClose} data-testid="drawer-close-button"
              className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] hover:text-[--tx-1] transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {loading && <LoadingState message="Loading profile..." />}
        {!loading && file && (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[
                { l: "Review state", v: <ReviewStateChip state={file.review_state} /> },
                { l: "Classification", v: <StatusChip tone="crimson" className="capitalize">{file.classification}</StatusChip> },
                { l: "Quality", v: <StatusChip tone={QUALITY_TONE[file.quality_status]} className="capitalize">{file.quality_status}</StatusChip> },
                { l: "Domain", v: <span className="text-xs text-[--tx-1] capitalize">{(file.business_domain || "").replace("_", " ")}</span> },
                { l: "Journey stage", v: <span className="text-xs text-[--tx-1] capitalize">{(file.journey_stage || "").replace("_", " ")}</span> },
                { l: "SHA-256", v: <span className="text-[10px] font-mono text-[--tx-3]">{(file.sha256 || "").slice(0, 16)}…</span> },
              ].map((it) => (
                <div key={it.l} className="rounded-lg bg-[--panel-dark] border border-[--divider] px-2.5 py-2">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1">{it.l}</div>
                  {it.v}
                </div>
              ))}
            </div>

            {file.profile_error && (
              <div className="rounded-lg border border-[--danger] bg-[--danger-soft] p-3 text-xs text-[--tx-1]" data-testid="drawer-profile-error">
                <AlertTriangle size={12} className="inline mr-1.5 text-[--danger]" />Profiling failed: {file.profile_error}
              </div>
            )}
            {!prof && !file.profile_error && (
              <div className="rounded-lg border border-[--hairline] bg-[--shell-2] p-3 text-xs text-[--tx-2]">
                Profiling in progress - this view refreshes when complete.
              </div>
            )}

            {prof?.kind === "tabular" && (
              <div className="space-y-3" data-testid="drawer-tabular-profile">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusChip tone="ai">{(prof.sheets.filter((s) => !s.is_empty).length || prof.sheets.length)} data {prof.sheets.filter((s) => !s.is_empty).length === 1 ? "sheet" : "sheets"}</StatusChip>
                  <StatusChip tone="ai">{fmtInt(prof.sheets.reduce((a, s) => a + s.row_count, 0))} rows</StatusChip>
                  {prof.table_count > 0 && <StatusChip tone="ai">{prof.table_count} {prof.table_count === 1 ? "table" : "tables"}</StatusChip>}
                  {prof.primary_sheet && <StatusChip tone="validated">primary: {prof.primary_sheet}</StatusChip>}
                  {prof.needs_review_count > 0 && <StatusChip tone="risk">{prof.needs_review_count} {prof.needs_review_count === 1 ? "sheet needs" : "sheets need"} review</StatusChip>}
                  {prof.normalized_records > 0 && <StatusChip tone="validated">{fmtInt(prof.normalized_records)} normalized metric records</StatusChip>}
                </div>
                {prof.sheets.slice(0, 40).map((s) => (
                  <SheetProfile key={s.sheet} sheet={s} fileId={fileId} onChanged={() => { load(); onChanged(); }} />
                ))}
              </div>
            )}

            {prof?.kind === "document" && (
              <div className="space-y-3" data-testid="drawer-document-profile">
                <div className="flex items-center gap-2">
                  {prof.page_count && <StatusChip tone="ai">{prof.page_count} pages</StatusChip>}
                  <StatusChip tone="ai">{fmtInt(prof.char_count)} characters extracted</StatusChip>
                  {prof.extracted_series?.length > 0 && <StatusChip tone="validated">{prof.extracted_series.length} monthly series detected</StatusChip>}
                </div>
                {prof.extracted_series?.map((s) => (
                  <div key={s.label} className="rounded-lg border border-[--hairline] bg-[--panel-dark] p-3">
                    <div className="text-xs font-semibold text-[--ai] mb-1.5">{s.label}</div>
                    <div className="overflow-x-auto thin-scroll">
                      <table className="text-[10px] font-mono text-[--tx-2]">
                        <tbody>
                          <tr>{s.months.map((m) => <td key={m} className="px-1.5 py-0.5 text-[--tx-3]">{m}</td>)}</tr>
                          <tr>{s.values.map((v, i) => <td key={i} className="px-1.5 py-0.5 tabular-nums text-[--tx-1]">{v.raw}</td>)}</tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                <div className="rounded-lg border border-[--hairline] bg-[--panel-dark] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Text excerpt</div>
                  <pre className="text-[10px] text-[--tx-2] whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto thin-scroll">{prof.excerpt}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

const SheetProfile = ({ sheet, fileId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [pinning, setPinning] = useState(false);
  const slug = sheet.sheet.replace(/\s+/g, "-").toLowerCase();

  const pinHeader = async (rowNum) => {
    try {
      setPinning(true);
      const res = await api.post(`/live/files/${fileId}/header-override`, { sheet: sheet.sheet, header_row: rowNum, actor_role: "six_lead" });
      toast.success(res.data.message);
      setTimeout(() => { onChanged && onChanged(); setPinning(false); }, 3500);
      setTimeout(() => onChanged && onChanged(), 8000);
    } catch (e) {
      setPinning(false);
      toast.error(e?.response?.data?.detail || "Could not set header row");
    }
  };

  if (sheet.is_empty) {
    return (
      <div className="rounded-lg border border-[--hairline] bg-[--panel-dark] opacity-60"
        data-testid={`sheet-empty-${slug}`}>
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-xs text-[--tx-3] truncate">{sheet.sheet}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] shrink-0">empty auxiliary worksheet</span>
        </div>
      </div>
    );
  }
  if (sheet.needs_review) {
    return (
      <div className="rounded-lg border border-[--amber] bg-[--amber-soft]" data-testid={`sheet-needs-review-${slug}`}>
        <div className="px-3 py-2.5 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-[--tx-1] truncate">{sheet.sheet}</span>
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[--amber] text-[--amber] shrink-0">{sheet.user_action || "Select header row"}</span>
          </div>
          <p className="text-[11px] text-[--tx-2] leading-relaxed">{sheet.exception}</p>
          {sheet.raw_preview?.length > 0 && (
            <div className="rounded-md border border-[--divider] bg-[--panel-dark] overflow-hidden">
              <div className="text-[9px] font-mono uppercase tracking-wider text-[--tx-3] px-2 py-1 border-b border-[--divider]">Raw preview - click a row to use it as the header</div>
              {sheet.raw_preview.map((r) => (
                <button key={r.row} onClick={() => pinHeader(r.row)} disabled={pinning}
                  data-testid={`sheet-${slug}-pick-header-${r.row}`}
                  className="w-full flex items-center gap-2 px-2 py-1 text-left border-b border-[--divider] last:border-b-0 hover:bg-white/[0.04] disabled:opacity-50 transition-colors">
                  <span className="text-[9px] font-mono text-[--amber] w-8 shrink-0">row {r.row}</span>
                  <span className="text-[10px] font-mono text-[--tx-2] truncate">{r.cells.filter(Boolean).join(" · ") || "(blank)"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-[--hairline] bg-[--panel-dark]">
      <button onClick={() => setOpen(!open)} data-testid={`sheet-toggle-${slug}`}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight size={12} className={`text-[--tx-3] transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="text-xs font-semibold text-[--tx-1] truncate">{sheet.sheet}</span>
          {sheet.primary && (
            <span data-testid={`sheet-primary-badge-${slug}`}
              className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[--ai] bg-[--ai-soft] text-[--ai] shrink-0">
              primary dataset
            </span>
          )}
          {sheet.tables?.length > 1 && (
            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-[--hairline] text-[--tx-3] shrink-0">{sheet.tables.length} tables</span>
          )}
        </div>
        <span className="text-[10px] font-mono text-[--tx-3] shrink-0">{fmtInt(sheet.row_count)} rows · {sheet.column_count} cols · dup {(sheet.duplicate_rate * 100).toFixed(1)}%</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono text-[--tx-3]" data-testid={`sheet-manifest-${slug}`}>
            {sheet.header_row && <span>header row <span className="text-[--tx-1]">{sheet.header_row}</span>{sheet.header_override ? " (pinned)" : ""}</span>}
            {sheet.first_data_row && <span>first data row <span className="text-[--tx-1]">{sheet.first_data_row}</span></span>}
            {sheet.table_name && sheet.table_name !== sheet.sheet && <span>table <span className="text-[--tx-1]">{sheet.table_name.slice(0, 34)}</span></span>}
            {sheet.coverage_start && <span>coverage <span className="text-[--tx-1]">{sheet.coverage_start} → {sheet.coverage_end}</span></span>}
            {sheet.detected_domain && <span>domain <span className="text-[--ai]">{sheet.detected_domain}</span></span>}
            {sheet.suggested_journey_stage && <span>journey <span className="text-[--ai]">{sheet.suggested_journey_stage}</span></span>}
            {sheet.quality_status && <span>quality <span className={sheet.quality_status === "good" ? "text-[--green]" : sheet.quality_status === "issues" ? "text-[--amber]" : "text-[--tx-1]"}>{sheet.quality_status}</span></span>}
          </div>
          {sheet.tables?.length > 1 && (
            <div className="text-[10px] font-mono text-[--tx-3]">
              <span className="uppercase tracking-wider mr-1.5 text-[--amber]">Tables in sheet</span>
              {sheet.tables.map((t) => `${t.name} (hdr r${t.header_row}, ${t.row_count} rows)`).join(" · ")}
            </div>
          )}
          {sheet.id_candidates?.length > 0 && (
            <div className="text-[11px] text-[--tx-2]"><span className="text-[--green] font-mono uppercase text-[10px] mr-1.5">ID candidates</span>{sheet.id_candidates.join(", ")}</div>
          )}
          {sheet.join_key_candidates?.length > 0 && (
            <div className="text-[11px] text-[--tx-2]"><span className="text-[--ai] font-mono uppercase text-[10px] mr-1.5">Join keys</span>{sheet.join_key_candidates.join(", ")}</div>
          )}
          {sheet.date_ranges?.length > 0 && (
            <div className="text-[11px] text-[--tx-2]"><span className="text-[--amber] font-mono uppercase text-[10px] mr-1.5">Date ranges</span>{sheet.date_ranges.map((d) => `${d.column}: ${d.min} → ${d.max}`).join(" · ")}</div>
          )}
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-left text-[--tx-3] font-mono uppercase">
                  <th className="py-1 pr-3">Column</th><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">Null</th><th className="py-1 pr-3">Unique</th><th className="py-1">Samples</th>
                </tr>
              </thead>
              <tbody>
                {sheet.columns.slice(0, 14).map((c) => (
                  <tr key={c.name} className="border-t border-[--divider] text-[--tx-2]">
                    <td className="py-1 pr-3 text-[--tx-1]">{c.name.slice(0, 26)}</td>
                    <td className="py-1 pr-3 font-mono">{c.dtype.slice(0, 10)}</td>
                    <td className="py-1 pr-3 tabular-nums">{(c.null_rate * 100).toFixed(0)}%</td>
                    <td className="py-1 pr-3 tabular-nums">{c.unique_count}</td>
                    <td className="py-1 truncate max-w-[180px]">{c.sample_values.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default function DataIntakeHubPage() {
  const { data: meta } = useFetch("/live/meta");
  const { data: files, loading, refetch } = useFetch("/live/files");
  const { data: requests, refetch: refetchReqs } = useFetch("/live/requests");
  const { data: auditLog, refetch: refetchAudit } = useFetch("/live/audit?limit=12");
  const [drawerId, setDrawerId] = useState(null);

  // Poll while any file is still profiling
  useEffect(() => {
    const pending = (files || []).some((f) => f.review_state === "received");
    if (!pending) return;
    const t = setInterval(() => { refetch(); refetchReqs(); refetchAudit(); }, 2500);
    return () => clearInterval(t);
  }, [files, refetch, refetchReqs, refetchAudit]);

  const refreshAll = () => { refetch(); refetchReqs(); refetchAudit(); };

  const [reprofiling, setReprofiling] = useState(false);
  const reprofileAll = async () => {
    try {
      setReprofiling(true);
      const res = await api.post("/live/files/reprofile-all", { actor_role: "six_lead" });
      toast.success(res.data.message);
      let ticks = 0;
      const t = setInterval(() => {
        refreshAll();
        if (++ticks >= 8) { clearInterval(t); setReprofiling(false); }
      }, 3000);
    } catch (e) {
      setReprofiling(false);
      toast.error(e?.response?.data?.detail || "Reprofile failed");
    }
  };

  const wipeLive = async () => {
    try {
      const res = await api.post("/live/reset", { actor_role: "six_lead", confirm: "RESET_LIVE" });
      toast.success(res.data.message);
      refreshAll();
    } catch (e) { toast.error("Reset failed"); }
  };

  return (
    <div data-testid="live-intake-page">
      <PageHeader
        kicker="Live engagement · intake"
        title="Data Intake Hub"
        subtitle="Secure ingestion for Brightspeed files. Every file is registered, profiled, and linked to the data-request register - and every downstream conclusion traces back to the originating file, tab, and columns."
        right={
          <div className="flex items-center gap-2">
            <LiveBadge />
            <button onClick={refreshAll} data-testid="intake-refresh-button" title="Refresh"
              className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--tx-1] transition-colors">
              <RefreshCcw size={13} />
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="intake-wipe-button" title="Wipe all live data"
                  className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--danger] transition-colors">
                  <Eraser size={13} />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[--shell-1] border-[--hairline]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[--tx-1]">Wipe the live workspace?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[--tx-2]">
                    Removes every uploaded file, mapping, calculation run, finding, hypothesis, and SOP from the live engagement. Demonstration content is untouched. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[--shell-3] border-[--hairline] text-[--tx-2]">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={wipeLive} data-testid="intake-wipe-confirm"
                    className="bg-[--six-crimson] hover:bg-[--six-crimson-hover] text-white">Wipe live data</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />

      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          <UploadPanel meta={meta} onUploaded={refreshAll} />

          <Panel title="File registry" testId="intake-registry-panel"
            right={
              <div className="flex items-center gap-2">
                <button onClick={reprofileAll} disabled={reprofiling || (files || []).length === 0}
                  data-testid="intake-reprofile-all-btn" title="Reprocess every file with the current profiler"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-[--tx-2] hover:text-[--ai] hover:border-[--ai] disabled:opacity-50 disabled:pointer-events-none transition-colors">
                  <RefreshCcw size={11} className={reprofiling ? "animate-spin" : ""} />
                  {reprofiling ? "Reprofiling…" : "Reprofile all"}
                </button>
                <span className="text-[10px] font-mono text-[--tx-3]">{(files || []).length} files</span>
              </div>
            }>
            {loading && <LoadingState message="Loading registry..." />}
            {!loading && (files || []).length === 0 && (
              <EmptyState message="No files registered yet. Drop the first Brightspeed extract above to begin." />
            )}
            {!loading && (files || []).length > 0 && (
              <div className="space-y-1.5">
                {files.map((f) => (
                  <button key={f.id} onClick={() => setDrawerId(f.id)} data-testid={`intake-file-row-${f.id}`}
                    className="w-full flex items-center gap-3 rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2.5 text-left hover:border-[--ai] active:scale-[0.995] transition-colors">
                    {f.kind === "tabular"
                      ? <FileSpreadsheet size={15} className="text-[--green] shrink-0" />
                      : <FileText size={15} className="text-[--ai] shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-[--tx-1] truncate">{f.filename}</div>
                      <SourceTag file={f} />
                    </div>
                    <div className="hidden md:block text-[10px] font-mono text-[--tx-3] shrink-0">
                      {f.profile_summary?.kind === "tabular" && `${f.profile_summary.sheet_count} ${f.profile_summary.sheet_count === 1 ? "sheet" : "sheets"}${f.profile_summary.primary_sheet ? ` (${f.profile_summary.primary_sheet})` : ""} · ${fmtInt(f.profile_summary.total_rows)} rows`}
                      {f.profile_summary?.kind === "document" && `${f.profile_summary.page_count || "?"} pages · ${f.profile_summary.series_found} series`}
                    </div>
                    <ReviewStateChip state={f.review_state} />
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-4">
          <Panel title="Data-request register" testId="intake-requests-panel"
            right={<span className="text-[10px] font-mono text-[--tx-3]">{(requests || []).filter((r) => r.status !== "missing").length}/{(requests || []).length} covered</span>}>
            <div className="space-y-2">
              {(requests || []).map((r) => (
                <div key={r.id} className="rounded-lg border border-[--hairline] bg-[--shell-2] p-2.5" data-testid={`intake-request-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[--tx-1]">{r.category}</span>
                    <StatusChip tone={REQUEST_TONE[r.status]} className="capitalize shrink-0">{r.status}</StatusChip>
                  </div>
                  {r.matched_file_names?.length > 0 ? (
                    <div className="text-[10px] text-[--tx-3] mt-1 truncate">↳ {r.matched_file_names.join(" · ")}</div>
                  ) : (
                    <div className="text-[10px] text-[--amber] mt-1 leading-snug">Needed: {r.follow_up}</div>
                  )}
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recent activity" testId="intake-audit-panel"
            right={<History size={12} className="text-[--tx-3]" />}>
            {(auditLog || []).length === 0 && <EmptyState message="No live activity yet." />}
            <div className="space-y-2">
              {(auditLog || []).map((a) => (
                <div key={a.id} className="flex items-start gap-2 text-[11px]">
                  <span className="font-mono text-[10px] text-[--tx-3] shrink-0 pt-px">{(a.ts || "").slice(5, 16).replace("T", " ")}</span>
                  <span className="text-[--tx-2] leading-snug"><span className="text-[--ai]">{a.action.replace(/_/g, " ")}</span> — {a.detail?.slice(0, 90)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {drawerId && <FileDrawer fileId={drawerId} onClose={() => setDrawerId(null)} onChanged={refreshAll} />}
    </div>
  );
}
